import clsx from 'clsx';
import React, { useEffect, useState } from 'react';

import { imageCache } from '@/src/cache/imageCache';

import SectionLoader from '../Spinner/SectionLoader/SectionLoader';

type ImageProps = React.ImgHTMLAttributes<HTMLImageElement> & {
  src: string;
  fallback?: JSX.Element;
  showLoader?: boolean;
};

const Image = ({ src, alt, onLoad, fallback = null, showLoader = true, ...rest }: ImageProps) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const loadFromCache = async () => {
      try {
        const cachedImage = await imageCache.read(src);
        setIsLoaded(true);
        return cachedImage;
      } catch (error) {
        setHasError(true);
        onLoad(null);
        throw error;
      }
    };

    loadFromCache();
  }, [src]);

  const handleImageLoaded = () => {
    setIsLoaded(true);
    onLoad(null);
  };

  const handleImageError = () => {
    setHasError(true);
    onLoad(null);
  };

  return (
    <React.Fragment>
      {showLoader && !isLoaded && !hasError && (
        <div
          className={clsx(
            'tw-absolute tw-top-0 tw-left-0',
            'tw-w-full tw-h-full',
            'tw-flex tw-items-center tw-justify-center',
            'tw-bg-cfo-grey-100'
          )}
        >
          <SectionLoader />
        </div>
      )}

      <img
        className={clsx(isLoaded ? 'tw-block' : 'tw-hidden')}
        src={src}
        alt={alt || ''}
        onLoad={handleImageLoaded}
        onError={handleImageError}
        {...rest}
      />

      {!fallback && hasError && (
        <div
          className={clsx(
            'tw-absolute tw-top-0 tw-left-0',
            'tw-w-full tw-h-full',
            'tw-flex tw-items-center tw-justify-center',
            'tw-bg-cfo-grey-100'
          )}
        >
          Image failed to load
        </div>
      )}

      {fallback && hasError && <React.Fragment>{fallback}</React.Fragment>}
    </React.Fragment>
  );
};

export default Image;
